import React, { useState, useEffect, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress, createFilterOptions, Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Tooltip, IconButton } from '@mui/material';
import supabase from '../../../../../utils/supabase';
import debounce from 'lodash/debounce';
import AddIcon from '@mui/icons-material/Add';

const filter = createFilterOptions();

export default function ResearcherSelector({ label, value, onChange, returnObject = false, ...props }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newResearcher, setNewResearcher] = useState({ last_name: '', first_name: '', middle_name: '', orcid: '' });

  const searchResearchers = async (input) => {
    const trimmed = input.trim();
    if (!trimmed) return [];

    // 1. Try RPC first (Server-side search)
    try {
      const { data, error } = await supabase.rpc('search_researchers', { keyword: trimmed });
      if (!error && data) return data;
    } catch (e) {
      // RPC failed or not found, fall back to client-side logic
      console.warn('RPC search_researchers failed, falling back to client-side search.', e);
    }

    // 2. Fallback: Client-side search (Researchers + Aliases)
    const terms = trimmed.split(/\s+/).filter(Boolean);
    if (terms.length === 0) return [];

    // Fetch candidates matching ANY term from researchers table
    let queryR = supabase.from('researchers').select('id, last_name, first_name, middle_name');
    const orConditionsR = terms.map(term => `last_name.ilike.%${term}%,first_name.ilike.%${term}%,middle_name.ilike.%${term}%`).join(',');
    queryR = queryR.or(orConditionsR).limit(50);
    
    // Fetch candidates matching ANY term from aliases table
    let queryA = supabase.from('researcher_aliases').select('researcher_id');
    const orConditionsA = terms.map(term => `alias_name.ilike.%${term}%`).join(',');
    queryA = queryA.or(orConditionsA).limit(50);

    const [resR, resA] = await Promise.all([queryR, queryA]);
    
    const researchers = resR.data || [];
    const aliasResearcherIds = (resA.data || []).map(a => a.researcher_id);

    // If we found matches in aliases that are not in researchers list, fetch them
    const missingIds = aliasResearcherIds.filter(id => !researchers.some(r => r.id === id));
    if (missingIds.length > 0) {
      const { data: extraResearchers } = await supabase
        .from('researchers')
        .select('id, last_name, first_name, middle_name')
        .in('id', missingIds);
      if (extraResearchers) {
        researchers.push(...extraResearchers);
      }
    }

    // Deduplicate
    const uniqueResearchers = Array.from(new Map(researchers.map(r => [r.id, r])).values());

    // Client-side filtering: Ensure ALL terms match (AND logic simulation)
    // We check if the researcher's name fields match all terms. 
    // Note: This fallback is imperfect because we don't have the alias name here to check against.
    // Ideally, we would fetch aliases for these researchers to do a full check, but that's expensive.
    // We'll assume if it was returned by the alias query, it matched at least one term.
    // For strict "AND" logic across multiple terms where some match name and some match alias, RPC is required.
    // Here we just return the unique candidates found by OR logic.
    return uniqueResearchers;
  };

  const fetchResearchers = useMemo(
    () =>
      debounce((input, callback) => {
        if (!input) {
          callback([]);
          return;
        }
        setLoading(true);
        searchResearchers(input).then((data) => {
          setLoading(false);
          callback(data || []);
        }).catch(err => {
          setLoading(false);
          console.error(err);
          callback([]);
        });
      }, 400),
    []
  );

  useEffect(() => {
    let active = true;
    fetchResearchers(inputValue, (results) => {
      if (active) {
        setOptions(results || []);
      }
    });
    return () => {
      active = false;
    };
  }, [inputValue, fetchResearchers]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    const found = options.find(opt => opt.id === value);
    return found || null;
  }, [options, value]);

  // value (ID) があるが options に含まれていない場合、サーバーから取得して表示する
  useEffect(() => {
    if (value && typeof value !== 'object' && !options.some(o => o.id === value)) {
      supabase.from('researchers')
        .select('id, last_name, first_name, middle_name')
        .eq('id', value)
        .single()
        .then(({ data }) => {
          if (data) setOptions(prev => [data, ...prev]);
        });
    }
  }, [value, options]);

  const handleAdd = async () => {
    if (!newResearcher.last_name) return;
    const payload = {
      last_name: newResearcher.last_name,
      first_name: newResearcher.first_name || null,
      middle_name: newResearcher.middle_name || null,
      orcid: newResearcher.orcid || null,
    };

    const { data, error } = await supabase.from('researchers').insert([payload]).select().single();
    if (error) {
      console.error("Error creating researcher:", error);
      alert("Failed to create researcher: " + error.message);
    } else if (data) {
      setOptions(prev => [data, ...prev]);
      onChange(returnObject ? data : data.id);
      setOpenAddDialog(false);
      setNewResearcher({ last_name: '', first_name: '', middle_name: '', orcid: '' });
    }
  };

  const handleOpenDialog = () => {
    setNewResearcher({ last_name: '', first_name: '', middle_name: '', orcid: '' });
    setOpenAddDialog(true);
  };

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        <Autocomplete
          {...props}
          sx={{ flexGrow: 1 }}
          options={options}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            if (option.inputValue) return `Add "${option.inputValue}"`;
            return `${option.last_name || ''}, ${option.first_name || ''} ${option.middle_name || ''}`.trim();
          }}
          value={selectedOption}
          onChange={(event, newValue) => {
            if (typeof newValue === 'string') {
              setNewResearcher({ last_name: newValue, first_name: '', middle_name: '', orcid: '' });
              setOpenAddDialog(true);
            } else if (newValue && newValue.inputValue) {
              const parts = newValue.inputValue.split(/,| /).map(s => s.trim()).filter(Boolean);
              const last = parts[0] || newValue.inputValue;
              const first = parts.slice(1).join(' ') || '';
              setNewResearcher({ last_name: last, first_name: first, middle_name: '', orcid: '' });
              setOpenAddDialog(true);
            } else {
              onChange(newValue ? (returnObject ? newValue : newValue.id) : null);
            }
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            const { inputValue } = params;
            const isExisting = options.some((option) => 
              `${option.last_name || ''}, ${option.first_name || ''}`.trim().toLowerCase() === inputValue.toLowerCase()
            );
            if (inputValue !== '' && !isExisting && !loading) {
              filtered.push({
                inputValue,
                last_name: `Add "${inputValue}"`,
                first_name: '',
                id: 'add-new-researcher-option'
              });
            }
            return filtered;
          }}
          onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
          loading={loading}
          renderInput={(params) => <TextField {...params} label={label} size="small" InputProps={{ ...params.InputProps, endAdornment: <>{loading ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</> }} />}
        />
        <Tooltip title="Add new researcher">
          <IconButton onClick={handleOpenDialog}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
        <DialogTitle>Add New Researcher</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1, minWidth: 300 }}>
            <TextField label="Last Name" value={newResearcher.last_name} onChange={(e) => setNewResearcher(prev => ({ ...prev, last_name: e.target.value }))} fullWidth autoFocus />
            <TextField label="First Name" value={newResearcher.first_name} onChange={(e) => setNewResearcher(prev => ({ ...prev, first_name: e.target.value }))} fullWidth />
            <TextField label="Middle Name" value={newResearcher.middle_name} onChange={(e) => setNewResearcher(prev => ({ ...prev, middle_name: e.target.value }))} fullWidth />
            <TextField label="ORCID" value={newResearcher.orcid} onChange={(e) => setNewResearcher(prev => ({ ...prev, orcid: e.target.value }))} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button onClick={handleAdd} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
